/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import Papa from 'papaparse';

/**
 * CSV reading for our upload dialogs, on PapaParse for the field grammar — quoted fields holding
 * delimiters, escaped quotes and line breaks are its problem, not ours. What it does not give us is
 * bookkeeping: which physical line a record started on (so a wrong file is reported in terms the admin
 * can find again), comment lines, and which of the known separators a file uses. That bookkeeping is
 * what lives here.
 *
 * It is a reading layer only — trimming, column naming and what a row means stay with the callers. On
 * the writing side, `exportToCsv` (analytics.utils) and the other writers quote and escape as they
 * should, so files we produce parse back cleanly too.
 */

/** A record: one logical row of decoded fields, plus the line it starts on. */
export interface CsvRecord {
  /** 1-based line the record starts on — how an admin finds it again in the spreadsheet. */
  lineNumber: number;
  /** Decoded field values, in file order. */
  fields: string[];
}

/** The prefix a comment line carries in our CSV dialect. */
export const CSV_COMMENT_PREFIX = '#';

/**
 * Splits a single line into decoded fields: wrapping quotes unwrapped, `""` read as a literal quote,
 * delimiters inside a quoted field kept to their field. For probing one line — a header row, say —
 * where a whole-file read would fetch more than the line holds.
 */
export function parseCsvRow(line: string, delimiter: string): string[] {
  return Papa.parse<string[]>(line, { delimiter }).data[0] ?? [];
}

/**
 * Reads a whole file into records. A line break inside a quoted field belongs to the field rather than
 * ending the record, so a quoted note or name cannot tear one record into two. Blank (or whitespace-only)
 * lines and lines opening with `#` — after decoding, so the template's quoted notes still read as
 * comments — are dropped.
 *
 * PapaParse is driven a row at a time for the line numbers: each step hands back the offset the row ended
 * on, and a running count of the breaks those offsets span says which line the next record starts on.
 */
export function parseCsv(text: string, delimiter: string, commentPrefix: string = CSV_COMMENT_PREFIX): CsvRecord[] {
  const records: CsvRecord[] = [];
  const normalized = text.replace(/\r\n?/g, '\n');
  /** Breaks consumed before the record Papa is currently handing us. */
  let breaksBefore = 0;
  /** Offset the previous record ended on, where this one starts. */
  let rowStart = 0;

  Papa.parse<string[]>(normalized, {
    delimiter,
    // Blank lines come through as rows and are filtered below: with skipping on, Papa passes over them
    // without a step, and the cursor gap would take their line numbers with it.
    step: (row) => {
      const cursor = row.meta.cursor;
      const lineBreaks = countLineBreaks(normalized.slice(rowStart, cursor));
      records.push({ lineNumber: breaksBefore + 1, fields: row.data ?? [] });
      breaksBefore += lineBreaks;
      rowStart = cursor;
    },
  });

  return records.filter((record) => {
    if (record.fields[0]?.startsWith(commentPrefix)) {
      return false;
    }
    return !(record.fields.length === 1 && record.fields[0].trim() === '');
  });
}

/**
 * Picks the file's delimiter: the candidate appearing most often outside quoted spans. Names and
 * addresses contain none of the candidates, so on a template-shaped row the real delimiter scores
 * highest; inside quotes is where a name's commas live, and those must not vote.
 */
export function detectCsvDelimiter(text: string, candidates: readonly string[]): string {
  return candidates.reduce((best, candidate) => (countOutsideQuotes(text, candidate) > countOutsideQuotes(text, best) ? candidate : best));
}

/** Counts line breaks, whichever of the three endings a spreadsheet writes. */
function countLineBreaks(text: string): number {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n' || (text[i] === '\r' && text[i + 1] !== '\n')) {
      count++;
    }
  }
  return count;
}

/** Counts occurrences of `char` outside quoted spans of the text. */
function countOutsideQuotes(text: string, char: string): number {
  let count = 0;
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      if (inQuotes && text[i + 1] === '"') {
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes && c === char) {
      count++;
    }
  }
  return count;
}
